import { isMaintenanceError } from "@/api/errors";
import type { ErrorInfo, ReactNode } from "react";
import { Component } from "react";
import { MaintenanceMode } from "./MaintenanceMode";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  isMaintenanceError: boolean;
  maintenanceMessage?: string;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      isMaintenanceError: false
    };
  }

  static getDerivedStateFromError(error: Error): State {
    if (isMaintenanceError(error)) {
      return {
        hasError: true,
        isMaintenanceError: true,
        maintenanceMessage: error.message
      };
    }

    return {
      hasError: true,
      isMaintenanceError: false
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("Error caught by boundary:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      if (this.state.isMaintenanceError) {
        return <MaintenanceMode message={this.state.maintenanceMessage} />;
      }

      // Generic error fallback
      return (
        <div className="fixed inset-0 bg-gray-900 flex items-center justify-center">
          <div className="bg-gray-800 p-8 rounded-lg shadow-xl max-w-md w-full mx-4 text-center">
            <h1 className="text-2xl font-bold text-white mb-4">Something went wrong</h1>
            <p className="text-gray-300 mb-6">Please refresh the page to try again.</p>
            <button
              onClick={() => window.location.reload()}
              className="bg-blue-500 hover:bg-blue-600 text-white font-bold py-2 px-6 rounded"
            >
              Refresh Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
